import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";

/**
 * Generic searchable select / command-palette dropdown.
 * Props:
 *  - options: [{ value: string, label: string, hint?: string, icon?: ReactNode }]
 *  - value: current value
 *  - onChange: (value) => void
 *  - placeholder: trigger placeholder
 *  - searchPlaceholder
 *  - dark: boolean (dark trigger style for dashboard)
 *  - className, testid
 */
export function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  dark = false,
  className = "",
  testid,
  disabled = false,
  footer = null,
}) {
  const [open, setOpen] = useState(false);
  const closePopover = () => setOpen(false);
  const current = useMemo(() => options.find((o) => o.value === value), [options, value]);

  const triggerCls = dark
    ? "bg-[#10141C] border border-white/10 text-white hover:border-white/30 focus:border-[#FFD600]"
    : "bg-white border border-neutral-300 text-[#0A0C10] hover:border-[#0A0C10] focus:border-[#002FA7]";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={`w-full inline-flex items-center justify-between gap-2 px-4 py-2.5 text-sm
            outline-none transition-colors ${triggerCls} ${className} disabled:opacity-60`}
        >
          <span className="inline-flex items-center gap-2 truncate">
            {current?.icon}
            <span className={current ? "" : dark ? "text-neutral-500" : "text-neutral-400"}>
              {current?.label || placeholder}
            </span>
          </span>
          <ChevronsUpDown size={14} className="opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={`p-0 w-[var(--radix-popover-trigger-width)] ${dark ? "bg-[#10141C] border-white/10 text-white" : ""}`}
        align="start"
      >
        <Command className={dark ? "bg-[#10141C] text-white" : ""}>
          <CommandInput placeholder={searchPlaceholder} className={dark ? "text-white placeholder:text-neutral-500" : ""} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.hint || ""} ${o.value}`}
                  onSelect={() => {
                    onChange?.(o.value);
                    setOpen(false);
                  }}
                  data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                  className={dark ? "data-[selected=true]:bg-white/10 text-white" : ""}
                >
                  <Check size={14} className={`mr-2 ${value === o.value ? "opacity-100" : "opacity-0"}`} />
                  {o.icon && <span className="mr-2 inline-flex items-center">{o.icon}</span>}
                  <span className="flex-1 truncate">{o.label}</span>
                  {o.hint && <span className={`ml-2 text-xs ${dark ? "text-neutral-400" : "text-neutral-500"}`}>{o.hint}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        {footer && (
          <div className={`border-t px-1 py-1 ${dark ? "border-white/10" : "border-neutral-100"}`}>
            {typeof footer === "function" ? footer(closePopover) : footer}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
